import { createContext, useContext, useEffect, useState } from 'react';
import api from '../lib/api';

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const load = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load('vq_cart'));
  const [compare, setCompare] = useState(() => load('vq_compare'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { localStorage.setItem('vq_cart', JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem('vq_compare', JSON.stringify(compare)); }, [compare]);

  const add = (product, qty = 1, variant = null) => {
    const key = variant ? `${product.id}:${variant.id}` : product.id;
    const price = variant ? variant.retail_price : (product.effective_price || product.retail_price);
    const strength = variant ? variant.strength : product.strength;
    setItems((prev) => {
      const i = prev.findIndex((x) => x.key === key);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], quantity: Math.min(99, next[i].quantity + qty) };
        return next;
      }
      return [...prev, {
        key, id: product.id, variant_id: variant?.id || '', name: product.name, strength,
        format: product.format, price, image: product.primary_image, quantity: qty,
      }];
    });
    api.post('/analytics/event', { type: 'basket_add', product_id: product.id }).catch(() => {});
    setDrawerOpen(true);
  };

  const remove = (key) => setItems((prev) => prev.filter((x) => x.key !== key));
  const setQty = (key, quantity) =>
    setItems((prev) => prev.map((x) => (x.key === key ? { ...x, quantity: Math.max(1, Math.min(99, quantity)) } : x)));
  const clear = () => setItems([]);

  const count = items.reduce((s, x) => s + x.quantity, 0);
  const subtotal = items.reduce((s, x) => s + x.price * x.quantity, 0);

  const toggleCompare = (product) => {
    setCompare((prev) => {
      if (prev.find((x) => x.id === product.id)) return prev.filter((x) => x.id !== product.id);
      if (prev.length >= 4) return prev;
      return [...prev, {
        id: product.id, name: product.name, slug: product.slug,
        strength: product.has_variants ? `${product.variant_count} strengths` : product.strength,
        format: product.format, category: product.category,
        price: product.price_from ?? product.effective_price ?? product.retail_price,
        has_variants: !!product.has_variants,
        image: product.primary_image, coa_available: product.coa_available,
        batch_number: product.batch_number, status: product.status, specification: product.specification,
      }];
    });
    api.post('/analytics/event', { type: 'compare_add', product_id: product.id }).catch(() => {});
  };
  const inCompare = (id) => compare.some((x) => x.id === id);
  const clearCompare = () => setCompare([]);

  return (
    <CartCtx.Provider value={{
      items, add, remove, setQty, clear, count, subtotal,
      drawerOpen, setDrawerOpen, compare, toggleCompare, inCompare, clearCompare,
    }}>
      {children}
    </CartCtx.Provider>
  );
}
